<template>
  <modal
    :id="modalId"
    ref="modal"
    class="add-actor-to-article__modal with-subtitle"
    @close="close"
    :title="$t('add_organisation_manually')"
    is-simplified-modal
  >
    <template v-slot:title>
      <h2 v-if="!showSuccessState" class="title">You are inviting a new community member</h2>
      <h2 v-else class="title">
        Invitation Successful
      </h2>
    </template>

    <template v-slot:body>
      <form-group class="add-actor-to-article__modal-content" v-if="showSuccessState">
        {{ invitation.user.label }} was successfully invited to {{ community.name }}
      </form-group>

      <form-group v-if="!showSuccessState" label="Search a user by name or email address" required class="add-actor-to-article__modal-content">
        <autocomplete-dropdown
          is-simplified
          placeholder="Enter an email address or name"
          v-model="invitation.user"
          class="add-actor-to-article__dropdown"
          :required="true"
          :custom-no-result-message="'No user found. Press Enter to invite by email'"
          :options="displayedUserSuggestions"
          :do-search="autocompleteSearchTerm"
          :multiple="false"
          :allowClear="true"
          :margin-top="'0px'"
          :noResultsHandlerEnabled="true"
          :noResultMessage="{ text: 'Invite by email', smallText: 'Invite new user' }"
          @update:modelValue="verifyMember"
          @addNewItem="handleEmailInvitation"
          @search-change="handleSearchChange"
        />
        <p class="error">{{ error }}</p>
        <!--<p v-if="invitation.isEmailInvitation" class="info">
          You are inviting: <strong>{{ invitation.user.label }}</strong>
        </p>
        <p v-else-if="!invitation.user && currentSearchTerm && isValidEmail(currentSearchTerm)" class="info">
          Press "Invite community member" to send invitation to: <strong>{{ currentSearchTerm }}</strong>
        </p>-->
      </form-group>

      <form-group v-if="!showSuccessState" label="Personal message" class="add-actor-to-article__modal-content">
        <ds-textarea is-simplified v-model="invitation.personal_message" placeholder="Personal message"/>
      </form-group>
    </template>

    <template v-slot:footer>
      <div v-if="!showSuccessState" class="add-member-to-community__modal-footer">
        <ds-button
          class="add-actor-to-article__button"
          label="Invite community member"
          size="extra-small"
          variant="rounded"
          :disabled="isSubmitDisabled"
          @click="submit"
        />
      </div>
    </template>
  </modal>
</template>


<script>
  import MODAL_IDS from '../../constants/modal-ids.js'
  import { MUTATION_TYPES as UI_MUTATION_TYPES } from '../../store/modules/ui.js'
  import Modal from './Modal.vue'
  import TranslationsMixin from '../../util/TranslationsMixin.js'
  import AutocompleteDropdown from '../Dropdown/AutocompleteDropdown.vue'
  import FormGroup from '../Form/FormGroup.vue'
  import DsInput from '../Form/DsInput.vue'
  import { searchUser } from '../../api/config'
  import { addCommunityMember, verifyMember } from '../../api/communities'
  import DsTextarea from '../Form/DsTextarea.vue'

  export default {
    data () {
      return {
        modalId: MODAL_IDS.ADD_MEMBER_TO_COMMUNITY_MODAL,
        message: '',
        actor: [],
        isLoading: false,
        isDisabled: true,
        currentSearchTerm: '',
        invitation: {
          user: null,
          personal_message: '',
          isEmailInvitation: false
        },
        error: '',
        displayedUserSuggestions: [],
        showSuccessState: false
      }
    },
    computed: {
      ecosystemDisplayName () {
        return this.$store.getters.ecosystemDisplayName
      },
      communityId () {
        return this.community.id
      },
      community () {
        return this.modalContext.community
      },
      modalContext () {
        return this.$store.state.ui.modalContext
      },
      config () {
        return this.$store.state.config
      },
      isSubmitDisabled () {
        if (this.isLoading) {
          return true
        }

        // If we have a user selected
        if (this.invitation.user) {
          // For email invitations, ensure it's a valid email
          if (this.invitation.isEmailInvitation) {
            return !this.isValidEmail(this.invitation.user.email)
          }
          // For existing users, ensure they have a userId and passed verification
          return !this.invitation.user.value || this.isDisabled
        }

        // If no user selected, check if current search term is a valid email
        if (this.currentSearchTerm && this.isValidEmail(this.currentSearchTerm)) {
          return false
        }

        return true
      }
    },
    methods: {
      isValidEmail (email) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        return emailRegex.test(email)
      },
      handleEmailInvitation (emailAddress) {
        // Reset previous states
        this.error = ''
        this.invitation.isEmailInvitation = false

        if (!this.isValidEmail(emailAddress)) {
          this.error = 'Please enter a valid email address'
          this.isDisabled = true
          return
        }

        // Create a user object for email invitation
        this.invitation.user = {
          label: emailAddress,
          value: null, // No user ID for email invitations
          email: emailAddress
        }
        this.invitation.isEmailInvitation = true
        this.isDisabled = false
      },
      handleSearchChange (searchTerm) {
        this.currentSearchTerm = searchTerm

        // Reset email invitation state when search changes
        if (this.invitation.isEmailInvitation) {
          this.invitation.isEmailInvitation = false
          this.isDisabled = true
        }

        // If the search term is a valid email and no results are expected,
        // prepare for email invitation
        if (searchTerm && this.isValidEmail(searchTerm)) {
          // Don't automatically set it up, but show a hint
          this.error = ''
        }
      },
      verifyMember () {
        this.error = null
        this.isDisabled = true

        if (!this.invitation.user) {
          return
        }

        // Skip verification for email invitations
        if (this.invitation.isEmailInvitation) {
          this.isDisabled = false
          return
        }

        // Only verify if it's an existing user (has value/userId)
        if (!this.invitation.user.value) {
          return
        }

        verifyMember({ communityId: this.communityId, userId: this.invitation.user.value })
          .then(response => {
            this.isDisabled = false
            this.error = response.message
          })
          .catch(response => {
            this.isDisabled = true
            this.error = response.error
          })
      },
      close () {
        this.$store.commit(UI_MUTATION_TYPES.SET_MODAL_CONTEXT, null)
        this.$store.commit(UI_MUTATION_TYPES.HIDE_MODAL, this.modalId)

        // Reset form state
        this.invitation.user = null
        this.invitation.personal_message = ''
        this.invitation.isEmailInvitation = false
        this.error = ''
        this.isDisabled = true

        this.$emit('close')
        this.showSuccessState = false
      },
      autocompleteSearchTerm (query) {
        return searchUser({
          query: query
        })
          .then(suggestions => {
            const options = []
            //suggestions = this.removeDuplicates(suggestions)

            suggestions
              .forEach(function (user) {
                options.push({
                  label: user.name + ' (' + user.email + ')',
                  value: user.id
                })
              })

            return options
          })
          .catch(err => {
            console.log(err)
          })
      },
      removeDuplicates (suggestions) {
        return suggestions
      },
      submit () {
        this.error = null

        // Check if user typed an email but didn't formally select it
        if (!this.invitation.user && this.currentSearchTerm && this.isValidEmail(this.currentSearchTerm)) {
          // Set up email invitation from search term
          this.handleEmailInvitation(this.currentSearchTerm)
        }

        // Ensure we have a valid invitation
        if (!this.invitation.user) {
          this.error = 'Please select a user or enter a valid email address'
          return
        }

        let data = {
          personal_message: this.invitation.personal_message
        }

        // Handle email invitations vs existing user invitations
        if (this.invitation.isEmailInvitation) {
          data.email = this.invitation.user.email
        } else {
          data.userId = this.invitation.user.value
        }

        addCommunityMember({ communityId: this.communityId, data: data })
          .then(response => {
            if (response.error) {
              this.error = response.error
              return
            }

            if (response.success) {
              this.showSuccessState = true
              this.$bus.emit('invited-community-member')
            }

            this.userId = null
            this.invitation.personal_message = null
          })
          .catch(error => {
            this.error = error.response?.data?.message || 'An error occurred while sending the invitation'
          })
      }
    },
    mounted () {

    },
    components: {
      Modal,
      AutocompleteDropdown,
      FormGroup,
      DsInput,
      DsTextarea
    },
    mixins: [TranslationsMixin]
  }
</script>


<style lang="scss" scoped>
  @import '../../../scss/_variables.scss';

  .add-actor-to-article__modal-content {
    box-shadow: none;
    border-radius: 0;
    margin: 10px;
    padding: 10px;
  }

  .add-actor-to-article__modal-label {
    font-size: 12px;
  }

  .error {
    color: $color-error;
  }

  .info {
    color: $color-primary;
    font-size: 14px;
    margin-top: 8px;
  }

  .with-subtitle.modal__simplified :deep(.modal__wrapper .modal__container .modal__body) {
    overflow: visible;
  }

  :deep(.multiselect__content-wrapper) {
    max-height: 84px !important;
  }

</style>
